// frontend/src/App.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Content from "./content";

const App = () => {
  const [musicList, setMusicList] = useState([]);
  const [musicToggle, setMusicToggle] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const shuffleQueueRef = useRef([]);
  const musicListRef = useRef([]);
  const audioRef = useRef(null);

  const shuffleArray = (array) => {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  };

  const getNextShuffledTrack = useCallback(() => {
    if (shuffleQueueRef.current.length === 0) {
      shuffleQueueRef.current = shuffleArray(musicListRef.current);
    }
    const nextFile = shuffleQueueRef.current.shift();
    return nextFile ? `/api/audio/${nextFile}` : null;
  }, []);

  const playNextTrack = useCallback(() => {
    const nextTrack = getNextShuffledTrack();
    if (nextTrack) {
      setCurrentTrack(nextTrack);
    }
  }, [getNextShuffledTrack]);

  // Fetch audio list with AbortController
  useEffect(() => {
    const controller = new AbortController();

    fetch("/api/audio/list", { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        setMusicList(data.files || []);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Error fetching audio list:", err);
        }
      });

    return () => controller.abort();
  }, []);

  // Sync musicListRef with musicList
  useEffect(() => {
    musicListRef.current = musicList;
  }, [musicList]);

  // Initialize shuffle queue when music is toggled on
  useEffect(() => {
    if (musicToggle && musicList.length > 0 && !currentTrack) {
      shuffleQueueRef.current = shuffleArray(musicList);
      playNextTrack();
    }
  }, [musicToggle, musicList, currentTrack, playNextTrack]);

  // Control audio playback - always mounted element, controlled via play/pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (musicToggle && currentTrack) {
      audio.src = currentTrack;
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, [musicToggle, currentTrack]);

  // Stable toggle handler
  const handleToggleMusic = useCallback(() => {
    setMusicToggle((prev) => !prev);
  }, []);

  return (
    <MainContainer>
      <Content
        toggleMusic={handleToggleMusic}
        musicPlaying={musicToggle}
      />
      {/* Audio element always mounted to prevent audio context leaks */}
      <audio
        ref={audioRef}
        onEnded={playNextTrack}
        onError={playNextTrack}
        style={{ display: "none" }}
      />
    </MainContainer>
  );
};

export default App;

// Styled Components
const MainContainer = styled(Box)`
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  background-color: #ffffff;
  z-index: 1;
`;
